import { Head } from 'vite-react-ssg';
import { Link } from "react-router-dom";
import { Shield, Lock, Eye, Server, CheckCircle, AlertTriangle, Chrome } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const IsDeepseekSafe = () => {
  const faqItems = [
    {
      question: "Is DeepSeek AI safe to use?",
      answer: "Yes, DeepSeek AI is generally safe to use. It includes content filtering, safety guidelines, and moderation systems. As an open-source model, its code is publicly auditable by security researchers worldwide."
    },
    {
      question: "Does DeepSeek collect my data?",
      answer: "When using the DeepSeek API or chat interface, standard data processing applies similar to other AI services. For maximum privacy, you can run DeepSeek models locally on your own hardware, keeping all data on-premise."
    },
    {
      question: "Is DeepSeek open source?",
      answer: "Yes, DeepSeek models are open-source under the MIT license. This means anyone can inspect the code, verify its behavior, and run it independently without sending data to external servers."
    },
    {
      question: "Where is DeepSeek based?",
      answer: "DeepSeek is a Chinese AI company based in Hangzhou, China. The company was founded in 2023 and has quickly become a major player in open-source AI development."
    },
    {
      question: "Can I run DeepSeek locally for privacy?",
      answer: "Yes, one of DeepSeek's biggest advantages is local deployment. You can download the model weights and run them on your own servers or computer, ensuring complete data privacy with no external API calls."
    },
    {
      question: "Is DeepSeek better than ChatGPT for privacy?",
      answer: "DeepSeek offers more privacy options than ChatGPT because it's open-source and can run locally. With ChatGPT, all conversations go through OpenAI's servers. With DeepSeek, you can choose between API access or fully local deployment."
    },
    {
      question: "Does DeepSeek have content filters?",
      answer: "Yes, DeepSeek includes content moderation and safety filters to prevent harmful outputs. However, as an open-source model, users running it locally can modify these settings at their own discretion."
    },
    {
      question: "Is DeepSeek secure for business use?",
      answer: "DeepSeek can be very secure for enterprise use, especially when deployed on-premise. Many businesses choose local deployment to maintain complete control over their data and comply with privacy regulations."
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Is DeepSeek Safe? Security & Privacy Guide",
    "description": "Comprehensive guide to DeepSeek AI safety, security, and privacy. Learn about data handling, local deployment, and enterprise security options.",
    "author": {
      "@type": "Organization",
      "name": "The Independent DeepSeek Guide"
    },
    "publisher": {
      "@type": "Organization",
      "name": "The Independent DeepSeek Guide"
    },
    "datePublished": "2025-01-01",
    "dateModified": "2025-12-20"
  };

  return (
    <>
      <Head>
        <title>Is DeepSeek Safe? 2026 Privacy, Security &amp; Data Guide</title>
        <meta 
          name="description" 
          content="Is DeepSeek AI safe in 2026? Honest guide to privacy, data handling, content filters and how to run DeepSeek locally for full control." 
        />
        <meta name="keywords" content="is deepseek safe, deepseek security, deepseek privacy, deepseek data, is deepseek secure, deepseek safety, deepseek trusted" />
        <link rel="canonical" href="https://deepseek.ai/is-deepseek-safe" />
        
        <meta property="og:title" content="Is DeepSeek Safe? Security & Privacy Guide" />
        <meta property="og:description" content="Complete guide to DeepSeek AI safety, security, and privacy. Learn about data handling and local deployment options." />
        <meta property="og:url" content="https://deepseek.ai/is-deepseek-safe" />
        <meta property="og:type" content="article" />
        <meta property="og:image" content="https://deepseek.ai/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Is DeepSeek Safe? Security & Privacy Guide" />
        <meta name="twitter:description" content="Complete guide to DeepSeek AI safety, security, and privacy. Learn about data handling and local deployment options." />
        <meta name="twitter:image" content="https://deepseek.ai/og-image.png" />
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
      </Head>

      <main className="min-h-screen pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        {/* Hero Section */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-6">
            <Shield className="h-8 w-8 text-green-600" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">Is DeepSeek Safe?</h1>
          <p className="text-xl text-muted-foreground">
            A comprehensive guide to DeepSeek AI security, privacy, and safety features. Understand how your data is handled and your options for maximum privacy.
          </p>
        </div>

        {/* Safety Overview */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-8">DeepSeek Safety Overview</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-6 rounded-xl bg-card shadow-lg border">
              <Lock className="h-10 w-10 text-green-500 mb-4" />
              <h3 className="text-xl font-semibold mb-3">Open Source Transparency</h3>
              <p className="text-muted-foreground">
                DeepSeek's code is publicly available for inspection. Security researchers can audit the codebase and verify its behavior.
              </p>
            </div>
            
            <div className="p-6 rounded-xl bg-card shadow-lg border">
              <Server className="h-10 w-10 text-blue-500 mb-4" />
              <h3 className="text-xl font-semibold mb-3">Local Deployment</h3>
              <p className="text-muted-foreground">
                Run DeepSeek entirely on your own hardware. No data leaves your infrastructure, ensuring complete privacy control.
              </p>
            </div>
            
            <div className="p-6 rounded-xl bg-card shadow-lg border">
              <Eye className="h-10 w-10 text-purple-500 mb-4" />
              <h3 className="text-xl font-semibold mb-3">Content Filtering</h3>
              <p className="text-muted-foreground">
                Built-in safety measures and content moderation to prevent harmful or inappropriate outputs.
              </p>
            </div>
          </div>
        </section>

        {/* Pros and Cons */}
        <section className="mb-16 max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8">Security Pros & Cons</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="p-6 rounded-xl bg-green-50 border border-green-200">
              <h3 className="text-xl font-semibold mb-4 flex items-center text-green-700">
                <CheckCircle className="h-6 w-6 mr-2" />
                Security Advantages
              </h3>
              <ul className="space-y-3 text-muted-foreground">
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  Open-source code allows public security auditing
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  Local deployment keeps all data on-premise
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  MIT license allows security modifications
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  No vendor lock-in or dependency on external APIs
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  Compliant with data sovereignty requirements
                </li>
              </ul>
            </div>

            <div className="p-6 rounded-xl bg-amber-50 border border-amber-200">
              <h3 className="text-xl font-semibold mb-4 flex items-center text-amber-700">
                <AlertTriangle className="h-6 w-6 mr-2" />
                Considerations
              </h3>
              <ul className="space-y-3 text-muted-foreground">
                <li className="flex items-start">
                  <AlertTriangle className="h-5 w-5 text-amber-500 mr-2 mt-0.5 flex-shrink-0" />
                  API usage involves standard cloud data processing
                </li>
                <li className="flex items-start">
                  <AlertTriangle className="h-5 w-5 text-amber-500 mr-2 mt-0.5 flex-shrink-0" />
                  Company based in China (relevant for some regulations)
                </li>
                <li className="flex items-start">
                  <AlertTriangle className="h-5 w-5 text-amber-500 mr-2 mt-0.5 flex-shrink-0" />
                  Local deployment requires technical expertise
                </li>
                <li className="flex items-start">
                  <AlertTriangle className="h-5 w-5 text-amber-500 mr-2 mt-0.5 flex-shrink-0" />
                  Open-source means filters can be removed locally
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Privacy Options */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-8">Your Privacy Options</h2>
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-6 rounded-xl bg-card shadow-lg border">
                <h3 className="text-xl font-semibold mb-3">Option 1: Use the API</h3>
                <p className="text-muted-foreground mb-4">
                  Use DeepSeek's cloud API for convenience. Data is processed on DeepSeek servers with standard security measures.
                </p>
                <p className="text-sm text-muted-foreground">
                  Best for: Quick prototyping, non-sensitive applications, developers who prefer convenience.
                </p>
              </div>

              <div className="p-6 rounded-xl bg-card shadow-lg border border-green-200">
                <h3 className="text-xl font-semibold mb-3">Option 2: Local Deployment</h3>
                <p className="text-muted-foreground mb-4">
                  Download and run DeepSeek on your own servers. All data stays within your infrastructure.
                </p>
                <p className="text-sm text-muted-foreground">
                  Best for: Enterprise, sensitive data, privacy-conscious users, regulatory compliance.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="mb-16 max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8">Frequently Asked Questions</h2>
          <Accordion type="single" collapsible className="w-full">
            {faqItems.map((item, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger className="text-left">{item.question}</AccordionTrigger>
                <AccordionContent>{item.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        {/* Related Resources */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-8">Related Resources</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link to="/blog/what-is-deepseek-ai" className="p-6 rounded-xl bg-card shadow-lg border hover:shadow-xl transition-shadow">
              <h3 className="text-xl font-semibold mb-2">What is DeepSeek AI?</h3>
              <p className="text-muted-foreground">Learn about DeepSeek's technology, mission, and capabilities.</p>
            </Link>
            <Link to="/deepseek-r1" className="p-6 rounded-xl bg-card shadow-lg border hover:shadow-xl transition-shadow">
              <h3 className="text-xl font-semibold mb-2">DeepSeek R1</h3>
              <p className="text-muted-foreground">Explore DeepSeek's advanced reasoning model.</p>
            </Link>
            <Link to="/deepseek-api" className="p-6 rounded-xl bg-card shadow-lg border hover:shadow-xl transition-shadow">
              <h3 className="text-xl font-semibold mb-2">DeepSeek API</h3>
              <p className="text-muted-foreground">Integrate DeepSeek into your applications.</p>
            </Link>
          </div>
        </section>

        {/* Conclusion */}
        <section className="text-center bg-primary/5 rounded-2xl p-12">
          <h2 className="text-3xl font-bold mb-4">The Bottom Line</h2>
          <p className="text-lg text-muted-foreground mb-6 max-w-2xl mx-auto">
            DeepSeek is safe for most use cases, especially when deployed locally. Its open-source nature provides transparency that proprietary models can't match. For sensitive applications, local deployment gives you complete control over your data.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a
              href="https://chromewebstore.google.com/detail/ai-sidebar-with-deepseek/inhcgfpbfdjbjogdfjbclgolkmhnooop"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-8 py-4 text-base font-medium rounded-full text-primary-foreground bg-primary hover:bg-primary/90 transition-all shadow-lg"
            >
              <Chrome className="mr-2 h-5 w-5" />
              Add to Chrome - It's Free
            </a>
            <Link
              to="/deepseek-vs-chatgpt"
              className="inline-flex items-center justify-center px-6 py-3 border-2 border-primary text-base font-medium rounded-full text-primary bg-background hover:bg-accent transition-all"
            >
              Compare with ChatGPT
            </Link>
          </div>
        </section>
      </main>
    </>
  );
};

export default IsDeepseekSafe;
